import React, { Fragment, useState } from 'react'
import { PreSaleSku, PreSaleSkuUpdate } from '../entities/PreSaleSku'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField
} from '@mui/material'
import { Filter } from '../../../components/_WMS/FilterComponent'
import { Field, Form, Formik } from 'formik'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal } from '../../../hooks/useModal'
import { updatePreSaleSku } from '../api'
import { useApi } from '../../../hooks/useApi'
import { Loading } from '../../../components/UI'
import { ConfirmActionInverted } from '../../../components/ConfirmAction/ConfirmActionInverted'

interface EditPreSaleSkuModalProps {
  preSaleSku: PreSaleSku
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
  requestPreSaleSku: (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => void
}

const INFINITE_QTY: number = 9999 // 9.999 es el valor de infinito, por lo tanto parte de ahí
const MAX_QTY: number = 99999

export const EditPreSaleSkuModal: React.FC<EditPreSaleSkuModalProps> = ({
  preSaleSku,
  handleSnackbar,
  handleCloseModal,
  requestPreSaleSku
}) => {
  const { callApi } = useApi()
  const [loading, setLoading] = useState<boolean>(false)

  const handleSubmit = async (
    values: PreSaleSku,
    newQty: undefined | number
  ) => {
    setLoading(true)

    const entity: PreSaleSkuUpdate = {
      isInfiniteQty: values.isInfiniteQty,
      qtyVirtualStock: newQty || values.qtyVirtualStock
    }

    callApi(updatePreSaleSku(values.skuId, entity))
      .then(() => {
        handleCloseModal()
        requestPreSaleSku()
        handleSnackbar('Producto editado correctamente', true)
      })
      .catch((error) => {
        handleSnackbar('Error al editar: ' + error, false)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const confirmModalState = useModal<{
    entity: PreSaleSku
    newQty: number | undefined
  }>('')

  const calculateNewStock = (
    operationType: string,
    adjustQty: number,
    baseQty: number
  ): number => {
    const adjustment = Number(adjustQty) || 0
    return operationType === 'sumar'
      ? baseQty + adjustment
      : baseQty - adjustment
  }

  const isSecondOportunity = () => {
    return !preSaleSku.isInfiniteQty // si llega como no infinito, significa que ya fue modificado alguna vez
  }

  const isDisableSaveButton = (qty: number, isSubmitting: boolean) => {
    return isSubmitting || qty > MAX_QTY || qty < 1
  }

  const validateNewQty = (value: number) => {
    const MAX_QTY = 99999
    const isTooHigh = value > MAX_QTY
    const isTooLow = value < 1
    const isError = isTooHigh || isTooLow

    let helperText = ''
    if (isTooHigh) helperText = 'El stock nuevo no puede superar 99.999'
    else if (isTooLow) helperText = 'El stock nuevo no puede ser menor a 1'

    return { isError, helperText }
  }

  if (loading) return <Loading />

  return (
    <Fragment>
      <Formik
        initialValues={{
          ...preSaleSku,
          operationType: 'sumar',
          adjustQty: 0,
          newQty: undefined,
          qtyVirtualStock: isSecondOportunity()
            ? preSaleSku.qtyVirtualStock
            : INFINITE_QTY
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const newQtyFinal = values.isInfiniteQty
              ? INFINITE_QTY
              : (Number(values.newQty) || 0) + Number(values.qtyReserved)

            if (newQtyFinal < values.qtyReserved) {
              handleSnackbar(
                'Error Cantidad nueva, no puede ser menor a lo reservado',
                false
              )
            } else {
              confirmModalState.openModal('xs', {
                entity: values,
                newQty: newQtyFinal
              })
            }
          } finally {
            setSubmitting(false)
          }
        }}
      >
        {({ isSubmitting, values, setFieldValue }) => {
          React.useEffect(() => {
            if (values.isInfiniteQty) {
              setFieldValue('newQty', INFINITE_QTY)
              return
            }

            const newQtyCalculated = calculateNewStock(
              values.operationType || 'sumar',
              values.adjustQty || 0,
              values.qtyVirtualStock || 0
            )

            setFieldValue('newQty', newQtyCalculated)
          }, [
            values.operationType,
            values.adjustQty,
            values.qtyVirtualStock,
            values.isInfiniteQty,
            setFieldValue
          ])
          return (
            <Form autoComplete='off'>
              <Grid container direction='row' spacing={2}>
                <Grid item xs={12}>
                  <Field name='isInfiniteQty' type='checkbox'>
                    {({ field }: any) => (
                      <FormControlLabel
                        control={
                          <Checkbox {...field} size='medium' color='primary' />
                        }
                        label='Stock Infinito'
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item xs={12}></Grid>
                {!values.isInfiniteQty && (
                  <Grid item xs={12}>
                    <Field name='qtyVirtualStock'>
                      {({ field }: any) => (
                        <TextField
                          {...field}
                          fullWidth
                          type='number'
                          label={
                            isSecondOportunity()
                              ? 'Stock Actual'
                              : 'Stock Venta en Verde'
                          }
                          variant='outlined'
                          disabled={isSecondOportunity()}
                          value={values.qtyVirtualStock || 0}
                        />
                      )}
                    </Field>
                  </Grid>
                )}
                {!values.isInfiniteQty && isSecondOportunity() && (
                  <Fragment>
                    <Grid item xs={12}>
                      <Field name='adjustQty'>
                        {({ field }: any) => (
                          <TextField
                            {...field}
                            fullWidth
                            label='Stock a Ajustar'
                            variant='outlined'
                            type='number'
                            value={values.adjustQty || 0}
                            inputProps={{
                              maxLength: 8,
                              onInput: (
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                // evita más de 8 dígitos
                                if (e.target.value.length > 8) {
                                  e.target.value = e.target.value.slice(0, 8)
                                }
                              }
                            }}
                          />
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name='operationType'>
                        {({ field }: any) => (
                          <FormControl component='fieldset' fullWidth>
                            <RadioGroup
                              name={field.name}
                              value={field.value || 'sumar'}
                              onChange={field.onChange}
                            >
                              <Box
                                display='flex'
                                justifyContent='space-evenly'
                                width='100%'
                                px={1}
                              >
                                <FormControlLabel
                                  value='sumar'
                                  control={<Radio />}
                                  label='Sumar'
                                />

                                <FormControlLabel
                                  value='restar'
                                  control={<Radio />}
                                  label='Restar'
                                />
                              </Box>
                            </RadioGroup>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    <Grid item xs={12}>
                      <Field name='newQty'>
                        {({ field }: any) => {
                          const value = Number(values.newQty || 0)
                          const { isError, helperText } = validateNewQty(value)

                          return (
                            <TextField
                              {...field}
                              fullWidth
                              type='number'
                              label='Stock Nuevo'
                              variant='outlined'
                              disabled
                              value={values.newQty || 0}
                              error={isError}
                              helperText={helperText}
                            />
                          )
                        }}
                      </Field>
                    </Grid>
                  </Fragment>
                )}

                <Grid item xs={6}>
                  <Box display='flex' justifyContent='flex-end'>
                    <Button
                      fullWidth
                      variant='contained'
                      color='primary'
                      type='submit'
                      disabled={isDisableSaveButton(
                        values.newQty || 0,
                        isSubmitting
                      )}
                    >
                      Guardar
                    </Button>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box display='flex' justifyContent='flex-start'>
                    <Button
                      fullWidth
                      variant='contained'
                      color='inherit'
                      type='button'
                      onClick={handleCloseModal}
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )
        }}
      </Formik>

      <Modal
        onClose={confirmModalState.closeModal}
        open={confirmModalState.isOpen}
        title={confirmModalState.title}
        maxWidth={confirmModalState.size}
      >
        <ConfirmActionInverted
          handleSubmitAction={() =>
            handleSubmit(
              confirmModalState.extendedProps?.entity as PreSaleSku,
              confirmModalState.extendedProps?.newQty
            )
          }
          handleCloseModal={confirmModalState.closeModal}
          confirmTitle={`Realizar este cambio, afectará el stock visible para la preventa del producto ¿desea continuar?`}
          confirmText='Confirmar'
          negateText='Volver atrás'
          isLoading={loading}
        />
      </Modal>
    </Fragment>
  )
}
